import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Event } from '../types/index.ts';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Calendar, MapPin, Ticket, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function Discover() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [suggestions, setSuggestions] = useState<Event[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadEvents();
  }, [selectedCategory, searchQuery]);

  const loadData = async () => {
    await Promise.all([loadEvents(), loadSuggestions()]);
  };

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (selectedCategory !== 'all' && selectedCategory !== 'virtual') {
        params.category = selectedCategory;
      }
      if (selectedCategory === 'virtual') {
        params.is_virtual = true;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const data = await api.getEvents(params);
      setEvents(data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const data = await api.getSuggestions();
      setSuggestions(data);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="space-y-4">
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
          Discover Events
        </h1>
        <p className="text-lg text-gray-400">Find your next amazing experience</p>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <Input
          placeholder="Search events, artists, venues..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 glass border-white/10 h-12"
        />
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="glass border-white/10">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="concert">Concerts</TabsTrigger>
          <TabsTrigger value="festival">Festivals</TabsTrigger>
          <TabsTrigger value="virtual">Virtual</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Suggested Events */}
      {suggestions.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-2xl font-heading font-bold">Suggested for You</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {suggestions.slice(0, 3).map((event) => (
              <EventCard key={event.event_id} event={event} onClick={() => navigate(`/event/${event.event_id}`)} isPrivileged={user?.role === 'admin' || user?.role === 'host'} />
            ))}
          </div>
        </section>
      )}

      {/* All Events */}
      <section className="space-y-4">
        <h2 className="text-2xl font-heading font-bold">All Events</h2>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="glass border-white/10 h-80 animate-pulse" />
            ))}
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <EventCard key={event.event_id} event={event} onClick={() => navigate(`/event/${event.event_id}`)} isPrivileged={user?.role === 'admin' || user?.role === 'host'} />
            ))}
          </div>
        ) : (
          <Card className="glass border-white/10 p-12 text-center">
            <p className="text-gray-400 text-lg">No events found. Try adjusting your filters.</p>
          </Card>
        )}
      </section>
    </div>
  );
}

function EventCard({ event, onClick, isPrivileged = false }: { event: Event; onClick: () => void; isPrivileged?: boolean }) {
  return (
    <Card
      className="glass border-white/10 overflow-hidden hover:border-white/20 transition-all cursor-pointer group hover:-translate-y-1"
      onClick={onClick}
    >
      {/* Event Image */}
      <div className="relative h-48 overflow-hidden bg-background-subtle">
        {event.poster_url || event.image ? (
          <img
            src={event.poster_url || event.image}
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar className="h-16 w-16 text-gray-600" />
          </div>
        )}
        {event.category && (
          <div className="absolute top-3 right-3">
            <span className="px-3 py-1 bg-primary rounded-full text-xs font-medium">
              {event.category}
            </span>
          </div>
        )}
      </div>

      <CardContent className="p-4 space-y-3">
        {/* Title */}
        <h3 className="font-heading font-bold text-lg line-clamp-1">{event.title}</h3>

        {/* Artist */}
        <p className="text-sm text-gray-400 line-clamp-1">{event.artist}</p>

        {/* Date & Location */}
        <div className="space-y-2 text-sm text-gray-400">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(event.date), 'MMM dd, yyyy • h:mm a')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4" />
            <span className="line-clamp-1">{event.venue}</span>
          </div>
        </div>

        {/* Price & Tickets (availability only for hosts/admins) */}
        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          {isPrivileged ? (
            <div className="flex items-center space-x-2">
              <Ticket className="h-4 w-4 text-primary" />
              <span className="text-sm text-gray-400">
                {event.capacity - event.tickets_sold} left
              </span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Ticket className="h-4 w-4 text-primary" />
              <span className="text-sm text-gray-400">Tickets available</span>
            </div>
          )}
          <span className="text-lg font-bold text-primary">
            {event.currency || '$'}{event.price}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}